"use client";

/**
 * "Bunu açanlar bunlara da baxıb" on the listing page (recommender plan,
 * section 6): GET /api/rec/similar/{id}, the listings people who opened
 * this one also opened, plus its closest neighbours in the same market.
 * Above the tabs of the residence,
 * the nearby listings and the look-alikes (ListingAroundTabs).
 *
 * A client island on a shared-cached page: nothing is in the HTML, the
 * read starts once the block is about to be seen (so after the page is
 * interactive), once per listing id, and the impressions are logged once
 * when the cards are on screen. No answer, or an empty one (no index
 * built yet), and the block never shows. Each card's link carries
 * `?via=similar&rec=<id>&pos=<n>` and data-rec / data-pos.
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { formatPrice } from "@/lib/api";
import { useT } from "@/lib/locale-context";
import { fetchRecSimilar, recData } from "@/lib/rec-client";
import { recHref, shownIds, type RecAnswer } from "@/lib/rec-feed";
import { useRecImpressions } from "@/lib/use-rec-impressions";
import { listingPath } from "@/lib/seo/paths";
import type { ListingCard } from "@/types";

const LIMIT = 6;
const SURFACE = "listing";

function facts(c: ListingCard, t: ReturnType<typeof useT>): string {
  return [
    c.room_count ? t("listings.magic.chip.rooms").replace("{n}", String(c.room_count)) : null,
    c.area_m2 ? `${Math.round(c.area_m2)} m²` : null,
    c.location_name ?? c.district ?? null,
  ].filter(Boolean).join(" · ");
}

export default function ListingRecSimilar({ listingId, active }: {
  listingId: string;
  /** A listing that is gone gets nothing (the index keeps live ones). */
  active: boolean;
}) {
  const t = useT();
  const ref = useRef<HTMLElement | null>(null);
  const [seen, setSeen] = useState<string | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      setSeen(listingId);
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [active, listingId]);

  const [ans, setAns] = useState<{ id: string; data: RecAnswer | null } | null>(null);
  useEffect(() => {
    if (seen !== listingId) return;
    const ctrl = new AbortController();
    let live = true;
    void fetchRecSimilar(listingId, LIMIT, ctrl.signal).then((data) => { if (live) setAns({ id: listingId, data }); });
    return () => { live = false; ctrl.abort(); };
  }, [seen, listingId]);

  const data = ans && ans.id === listingId ? ans.data : null;
  const items = (data?.items ?? []).filter((c) => listingPath(c.id)).slice(0, LIMIT);
  const req = data && items.length ? { rec: data.rec_request_id, algo: data.algo, ids: shownIds(items.map((card, pos) => ({ card, pos }))) } : null;
  useRecImpressions(ref, req, SURFACE);

  if (!data || !items.length) return <section ref={ref} aria-hidden />;

  return (
    <section ref={ref} className="space-y-3" aria-labelledby="listing-rec-similar">
      <h2 id="listing-rec-similar" className="text-lg font-bold tracking-tight text-gray-900">{t("rec.similar.title")}</h2>
      <ul className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
        {items.map((c, pos) => {
          const lp = listingPath(c.id)!;
          return (
            <li key={c.id} className="min-w-0" {...recData({ rec: data.rec_request_id, pos })}>
              <Link href={recHref(lp, "similar", data.rec_request_id, pos)} className="group block min-w-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
                <span className="block aspect-[4/3] overflow-hidden rounded-xl bg-gray-100">
                  {c.thumbnail_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={c.thumbnail_url} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" loading="lazy" />
                  ) : null}
                </span>
                <span className="mt-1.5 block truncate text-sm font-semibold tabular-nums text-gray-900">
                  {c.price != null ? `${formatPrice(c.price)} AZN${c.leased ? t("residence.live.month") : ""}` : t("common.na")}
                </span>
                <span className="block truncate text-[11px] text-gray-500">{facts(c, t)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
